import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { poolOrderApi, type PoolOrderListItem } from '../services/poolOrderApi';
import { UploadPoolOrderModal } from '../components/UploadPoolOrderModal';
import { MatchBar } from '../components/MatchBar';

export function PoolOrdersList() {
  const nav = useNavigate();
  const [items, setItems] = useState<PoolOrderListItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  async function reload() {
    try {
      const r = await poolOrderApi.list();
      setItems(r.pool_orders);
      setErr(null);
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to load');
    }
  }
  useEffect(() => { reload(); }, []);

  return (
    <div className="px-8 py-7 max-w-[1280px] mx-auto">
      <header className="flex items-end justify-between pb-6 border-b border-border">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">force pool tracking · weld county</div>
          <h1 className="font-display text-[34px] leading-tight">Pooling <em className="text-accent not-italic">orders</em></h1>
          <div className="text-[12px] text-text-dim mt-1.5 font-mono">
            {items === null ? 'loading…' : `${items.length} ingested · sorted by most recent`}
          </div>
        </div>
        <button onClick={() => setUploadOpen(true)}
          className="px-4 py-2 bg-accent text-bg text-[13px] font-medium rounded hover:bg-accent-bright">
          + Upload order
        </button>
      </header>

      <div className="mt-5 bg-bg-input/40 border border-border rounded px-4 py-3 text-[12px] text-text-dim">
        <strong className="text-text">Colorado pooling orders are name-only.</strong> No addresses or decimal interests are listed
        in COGCC orders. Matching the uncommitted owner names against your historical lease database is what makes them callable —
        unmatched names go to the watch list and get skip-trace ROI scoring.
      </div>

      {err && (
        <div className="mt-4 text-[12px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
      )}

      <div className="mt-6 bg-bg-card-soft border border-border rounded overflow-hidden">
        {items === null && !err && (
          <div className="px-5 py-8 text-[12px] text-text-faint font-mono">loading pool orders…</div>
        )}
        {items !== null && items.length === 0 && (
          <div className="px-5 py-10 text-center">
            <div className="text-[13px] text-text-dim">No pool orders ingested yet.</div>
            <div className="text-[11px] text-text-faint font-mono mt-1">Upload an ECMC application and interested-parties PDF to start.</div>
          </div>
        )}
        {items?.map(po => (
          <Link key={po.id} to={`/mi/pool-orders/${po.id}`}
            className="grid grid-cols-[120px_2fr_1.4fr_120px_1.4fr_90px_auto] items-center gap-4 px-5 py-4 border-b border-border last:border-b-0 hover:bg-bg-hover/40 transition-colors">
            <div>
              <div className="font-mono text-[15px] text-text">{po.docket_number ?? <span className="text-text-faint italic">no docket</span>}</div>
              <div className="font-mono text-[10px] text-text-faint mt-0.5">{po.cause_number ? `cause ${po.cause_number}` : ''} {po.application_type && <>· {po.application_type.toLowerCase()}</>}</div>
            </div>
            <div>
              <div className="text-[12px] text-text font-mono">
                {po.trs_keys.slice(0, 2).map(k => <span key={k} className="inline-block mr-2 mb-1 px-1.5 py-0.5 bg-bg-input/60 border border-border rounded text-[11px]">{k}</span>)}
                {po.trs_keys.length > 2 && <span className="text-text-faint">+{po.trs_keys.length - 2} more</span>}
              </div>
            </div>
            <div>
              <div className="text-[13px] text-text">{po.operator ?? <span className="text-text-faint italic">unknown</span>}</div>
              <div className="text-[10px] text-text-faint font-mono mt-0.5">
                {po.filed_date ? `filed ${po.filed_date}` : 'filed —'}
                {po.county && <> · {po.county.toLowerCase()}</>}
              </div>
            </div>
            <div>
              <div className="text-[14px] text-text font-mono"><em className="text-accent not-italic">{po.names_total}</em> names</div>
              <div className="text-[10px] text-text-faint font-mono mt-0.5">on order</div>
            </div>
            <div>
              <MatchBar matched={po.names_matched} suggested={po.names_suggested} unmatched={po.names_unmatched} />
              <div className="flex justify-between mt-1.5 text-[10px] font-mono">
                <span className="text-accent">{po.names_matched} matched</span>
                {po.names_suggested > 0 && <span className="text-accent/60">{po.names_suggested} suggested</span>}
                <span className="text-text-faint">{po.names_unmatched} unmatched</span>
              </div>
            </div>
            <div>
              {po.parse_status === 'complete'
                ? <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded bg-green/15 text-green">Parsed</span>
                : <span className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded bg-red/15 text-red">Failed</span>}
            </div>
            <div>
              <button className="px-3 py-1.5 border border-accent text-accent text-[12px] rounded hover:bg-accent-soft"
                onClick={e => { e.preventDefault(); nav(`/mi/pool-orders/${po.id}`); }}>
                Open →
              </button>
            </div>
          </Link>
        ))}
      </div>

      {uploadOpen && (
        <UploadPoolOrderModal
          onClose={() => setUploadOpen(false)}
          onIngested={(id) => { setUploadOpen(false); nav(`/mi/pool-orders/${id}`); }}
        />
      )}
    </div>
  );
}
